import {
  AppBar,
  Button,
  Grid,
  Paper,
  SelectChangeEvent,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material';
import usePagination from '@mui/material/usePagination/usePagination';
import { DocumentDownload } from 'asset';
import {
  FilterMenu,
  HeadTable,
  Layout,
  PopupConfirm,
  TableCaption,
} from 'components';
import { GetOptionsResponse } from 'components/form-control/Select';
import { drawerWidth } from 'config';
import FileSaver from 'file-saver';
import { NotifyService, getSortItem, hasPermission } from 'helpers';
import { useDateTime } from 'hooks';
import {
  FilterOption,
  FilterOptionType,
  FilterValue,
  GET_PARENTS_PARAMS_REQUEST,
  GET_REVIEW_LIST_PARAMS_REQUEST,
  GET_SITTERS_PARAMS_REQUEST,
  PARENT_TYPE,
  REVIEWS_TYPE,
  SITTER_ITEM_TYPE,
} from 'models';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import {
  getParentListApi,
  getReviewExportCSVApi,
  getSittersListApi,
  updatePublishReviewApi,
} from 'services';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setReviewsParams } from 'store/reducers/reviews';
import { getReviewsByAdminRequest } from 'store/reducers/reviews/actionTypes';
import { theme } from 'theme';
import { headCells } from './data';

const filterOptions: FilterOption[] = [
  {
    name: 'Parent',
    key: 'parent_id',
    type: FilterOptionType.SINGLE_SELECT,
  },
  {
    name: 'Sitter',
    key: 'sitter_id',
    type: FilterOptionType.SINGLE_SELECT,
  },
  {
    name: 'Number of stars',
    key: 'rate',
    type: FilterOptionType.SINGLE_SELECT,
  },
  {
    name: 'Date',
    key: 'created_date',
    type: FilterOptionType.DATE_PICKER,
  },
];

const Reviews = () => {
  const { formatDateTime } = useDateTime();
  const dispatch = useAppDispatch();
  const { reviews_by_admin } = useAppSelector(
    (state) => state.reviewsReducer.reviews
  );
  const { params } = reviews_by_admin;
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [itemReview, setItemReview] = useState<REVIEWS_TYPE>();

  const onSetListParams = (params: GET_REVIEW_LIST_PARAMS_REQUEST | null) => {
    dispatch(setReviewsParams(params));
  };

  useEffect(() => {
    if (params) {
      dispatch(getReviewsByAdminRequest(params));
    }
  }, [params, dispatch]);

  const handleChangeRowsPerPage = (event: SelectChangeEvent) => {
    const limit = parseInt(event.target.value, 10);
    onSetListParams({ ...params, page: 1, limit });
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  const { items } = usePagination({
    count: reviews_by_admin?.metadata.total_pages,
    page: reviews_by_admin?.metadata.page,
    onChange(_event, page) {
      onSetListParams({ ...params, page: page });
    },
  });

  const filterValue: FilterValue = useMemo(() => {
    return {
      parent_id: reviews_by_admin?.params?.parent_id,
      sitter_id: reviews_by_admin?.params?.sitter_id,
      rate: reviews_by_admin?.params?.rate,
      created_date: reviews_by_admin?.params?.created_date,
    };
  }, [reviews_by_admin?.params]);

  const onChangeFilter = (value: FilterValue) => {
    setSelected([]);
    const _params = { ...params };
    delete _params.parent_id;
    delete _params.sitter_id;
    delete _params.rate;
    delete _params.created_date;
    onSetListParams({
      ..._params,
      ...value,

      page: 1,
    });
  };

  const fetchDataOption = async (
    key: string,
    keyword?: string,
    limit: number = 20,
    page: number = 1
  ) => {
    let data: GetOptionsResponse | null = null;
    switch (key) {
      case 'parent_id':
        const paramsParent: GET_PARENTS_PARAMS_REQUEST = {
          page: page,
          limit: limit,
        };
        if (keyword) paramsParent.keyword = keyword;
        data = await getParentListApi(paramsParent)
          .then(({ data }) => {
            return {
              metadata: data.data.metadata,
              options: data.data.records.map((it: PARENT_TYPE) => ({
                id: it.parent_id,
                label: `${it.first_name} ${it.last_name}`,
              })),
            };
          })
          .catch((e) => {
            NotifyService.error(e);
            return null;
          });
        break;
      case 'sitter_id':
        const paramsSitter: GET_SITTERS_PARAMS_REQUEST = {
          page: page,
          limit: limit,
        };
        if (keyword) paramsSitter.keyword = keyword;
        data = await getSittersListApi(paramsSitter)
          .then(({ data }) => {
            return {
              metadata: data.data.metadata,
              options: data.data.records.map((it: SITTER_ITEM_TYPE) => ({
                ...it,
                id: it.sitter_id,
                label: `${it.first_name} ${it.last_name}`,
              })),
            };
          })
          .catch((e) => {
            NotifyService.error(e);
            return null;
          });
        break;
      case 'rate':
        data = {
          metadata: {
            page: page,
            limit: limit,
            page_count: 1,
            total_pages: 1,
            total_count: 1,
          },
          options: [
            {
              id: '1',
              label: '1',
            },
            {
              id: '2',
              label: '2',
            },
            {
              id: '3',
              label: '3',
            },
            {
              id: '4',
              label: '4',
            },
            {
              id: '5',
              label: '5',
            },
          ],
        };

        break;
    }

    return data;
  };

  const onExportCSV = async () => {
    await getReviewExportCSVApi()
      .then((res) => {
        FileSaver.saveAs(
          res.data,
          `${moment().format('YYYY-MM-DD')} Sitters.csv`
        );
      })
      .catch((e) => NotifyService.error(e));
  };

  const sortItem = useMemo(() => {
    return getSortItem(reviews_by_admin?.params?.order);
  }, [reviews_by_admin?.params]);

  const onOpenConfirmPublic = (item: REVIEWS_TYPE) => {
    setOpenConfirm(true);
    setItemReview(item);
  };

  const onSetPublish = async () => {
    if (itemReview) {
      const _data = {
        is_publish_google_review: !itemReview.is_publish_google_review,
      };
      await updatePublishReviewApi(itemReview.id, _data)
        .then(() => {
          NotifyService.success('Success');
          dispatch(getReviewsByAdminRequest(params));
          setOpenConfirm(false);
        })
        .catch((e) => {
          NotifyService.error(e);
        });
    }
  };

  return (
    <Layout>
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          backgroundColor: 'white.light',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
        elevation={0}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Grid>
            <Typography
              sx={{
                fontSize: '20px',
                lineHeight: 1,
                color: 'grey1.light',
              }}
            >
              Reviews
            </Typography>
          </Grid>
          <Grid display="flex" gap="8px" flexGrow={1} justifyContent="center">
            <FilterMenu
              filterOptions={filterOptions}
              filterValue={filterValue}
              onChange={onChangeFilter}
              fetchDataOption={fetchDataOption}
            />
          </Grid>
          <Grid display="flex" gap="8px">
            {hasPermission(['superadmin']) && (
              <Button
                sx={{
                  width: '131px',
                  height: 30,
                  fontWeight: 600,
                  fontSize: '13px',
                  border: `1px solid ${theme.palette.secondary.main}`,
                  p: '0px',
                }}
                variant="outlined"
                color="secondary"
                endIcon={<DocumentDownload />}
                onClick={onExportCSV}
              >
                Export to CSV
              </Button>
            )}
          </Grid>
        </Toolbar>
      </AppBar>
      <Stack spacing="8px">
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid #D9D9D9',
            maxHeight: 'calc(100vh - 150px)',
          }}
        >
          <Table
            stickyHeader
            sx={{ minWidth: 650 }}
            aria-labelledby="tableTitle"
          >
            <HeadTable<REVIEWS_TYPE>
              numSelected={selected.length}
              order={sortItem?.order}
              orderBy={sortItem?.orderBy}
              rowCount={reviews_by_admin?.rows.length}
              headCells={headCells}
              onAction={false}
              onCheckbox={false}
            />
            <TableBody
              sx={{
                height: 'auto !important',
              }}
            >
              {reviews_by_admin?.rows.map((row, index) => {
                const isItemSelected = isSelected(row?.id);

                return (
                  <TableRow
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row?.id}
                    selected={isItemSelected}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {`${row?.Parent?.User?.first_name || ''} ${
                        row?.Parent?.User?.last_name || ''
                      }`}
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row?.Parent?.User?.email}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {`${row?.Sitter?.User?.first_name} ${row?.Sitter?.User?.last_name}`}
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row?.Sitter?.User?.email}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row?.Job?.number}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row?.content}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row?.rate}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {formatDateTime(row?.createdAt)}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {hasPermission(['superadmin', 'staff']) ? (
                        <Switch
                          checked={row?.is_publish_google_review}
                          onChange={() => onOpenConfirmPublic(row)}
                        />
                      ) : row?.is_publish_google_review ? (
                        'Yes'
                      ) : (
                        'No'
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TableCaption
          rowsPerPage={params?.limit ?? 0}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          metadata={reviews_by_admin?.metadata}
          items={items}
        />
      </Stack>
      {openConfirm && (
        <PopupConfirm
          open
          title="Confirm action"
          description="Do you want to perform this action?"
          onClose={() => setOpenConfirm(false)}
          onConfirm={onSetPublish}
        />
      )}
    </Layout>
  );
};

export default Reviews;
